import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Rocket, Plus, Trash2, CheckCircle2, Loader2, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const EMPTY_FORM = { name: 'Shiprocket', email: '', password: '', pickup_location: 'Primary' };

export default function ShiprocketIntegrationPage() {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/couriers/integrations?courier_type=shiprocket`, authHeaders());
      setIntegrations((resp.data.integrations || []).filter((i) => i.courier_type === 'shiprocket'));
    } catch (e) {
      toast.error('Failed to load Shiprocket integrations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const testConnection = async () => {
    if (!form.email || !form.password) { toast.error('Enter API user email and password'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await axios.post(`${API}/couriers/test-connection`, {
        courier_type: 'shiprocket',
        credentials: { email: form.email, password: form.password, pickup_location: form.pickup_location },
        test_mode: false,
      }, authHeaders());
      setTestResult(resp.data);
      if (resp.data.success) toast.success('Shiprocket connection successful');
      else toast.error(resp.data.message || 'Connection failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection test failed');
    } finally { setTesting(false); }
  };

  const save = async () => {
    if (!form.email || !form.password) { toast.error('Enter API user email and password'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/couriers/integrations`, {
        courier_type: 'shiprocket',
        name: form.name || 'Shiprocket',
        credentials: { email: form.email, password: form.password, pickup_location: form.pickup_location || 'Primary' },
        test_mode: false,
        is_active: true,
      }, authHeaders());
      toast.success('Shiprocket account connected');
      setForm(EMPTY_FORM);
      setShowForm(false);
      setTestResult(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const toggleActive = async (integ) => {
    try {
      await axios.put(`${API}/couriers/integrations/${integ.id}`, { is_active: !integ.is_active }, authHeaders());
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const remove = async (integ) => {
    if (!window.confirm(`Delete Shiprocket account "${integ.name}"?`)) return;
    try {
      await axios.delete(`${API}/couriers/integrations/${integ.id}`, authHeaders());
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/integrations')} className="border-wt-border" data-testid="shiprocket-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600">
            <Rocket className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-wt-text-primary">Shiprocket</h1>
            <p className="text-wt-text-muted">Courier aggregator — book via 25+ courier partners, live rates, tracking</p>
          </div>
        </div>
        <Button className="ml-auto" onClick={() => setShowForm(!showForm)} data-testid="shiprocket-add-btn">
          <Plus className="w-4 h-4 mr-2" />Add Account
        </Button>
      </div>

      {showForm && (
        <Card className="bg-wt-paper border-wt-border">
          <CardHeader><CardTitle className="text-base text-wt-text-primary">Connect Shiprocket Account</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-wt-text-muted">
              Use an <b>API user</b> (Shiprocket → Settings → API → Configure → Create API user).
              It must be a different email from your main Shiprocket login.
              The token is managed automatically (10-day validity, auto-refresh).
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-wt-text-muted">Account label</Label>
                <Input value={form.name} className="mt-1" data-testid="shiprocket-name-input"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Pickup location name (as configured in Shiprocket)</Label>
                <Input value={form.pickup_location} className="mt-1" placeholder="Primary" data-testid="shiprocket-pickup-input"
                  onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">API user email</Label>
                <Input type="email" value={form.email} className="mt-1" data-testid="shiprocket-email-input"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">API user password</Label>
                <Input type="password" value={form.password} className="mt-1" data-testid="shiprocket-password-input"
                  onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            </div>
            {testResult && (
              <p className={`text-xs ${testResult.success ? 'text-emerald-400' : 'text-red-400'}`} data-testid="shiprocket-test-result">
                {testResult.success
                  ? `Connected — ${testResult.test_result?.couriers_available ?? 0} courier partners serviceable`
                  : testResult.message}
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="border-wt-border" onClick={testConnection} disabled={testing} data-testid="shiprocket-test-btn">
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}Test Connection
              </Button>
              <Button onClick={save} disabled={saving} data-testid="shiprocket-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Connect
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-wt-text-muted">Loading…</p>
      ) : integrations.length === 0 && !showForm ? (
        <Card className="bg-wt-paper border-wt-border">
          <CardContent className="py-10 text-center text-wt-text-muted text-sm" data-testid="shiprocket-empty-state">
            No Shiprocket account connected yet. Click "Add Account" to connect.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="shiprocket-accounts-list">
          {integrations.map((integ) => (
            <Card key={integ.id} className="bg-wt-paper border-wt-border">
              <CardContent className="flex items-center justify-between py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-wt-text-primary">{integ.name}</span>
                    {integ.is_active
                      ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 text-[10px]">Active</Badge>
                      : <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/40 text-[10px]">Inactive</Badge>}
                    {integ.is_default && <Badge variant="outline" className="text-[10px] border-wt-border">Default</Badge>}
                  </div>
                  <p className="text-xs text-wt-text-muted mt-1">
                    Pickup location: {integ.credentials?.pickup_location || 'Primary'} · Live rates + booking + tracking
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" className="h-8 text-xs border-wt-border"
                    onClick={() => navigate(`/integrations/shipping-rate-cards?open=${integ.id}`)}
                    data-testid={`shiprocket-rate-card-${integ.id}`}>
                    Rate Card
                  </Button>
                  <Switch checked={!!integ.is_active} onCheckedChange={() => toggleActive(integ)} data-testid={`shiprocket-toggle-${integ.id}`} />
                  <Button variant="ghost" size="icon" className="text-red-400" onClick={() => remove(integ)} data-testid={`shiprocket-delete-${integ.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
